import React from 'react';

const Section11Analytics = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-teal-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">📈</span>
      <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
        User Analytics & Feedback Loop
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">The closed loop that makes everything above get better over time</p>

    {/* Closed loop diagram */}
    <div className="bg-gradient-to-br from-teal-50/50 to-emerald-50/50 rounded-2xl p-6 border border-teal-200/30 mb-6">
      <h3 className="text-base font-bold text-teal-600 uppercase tracking-wider mb-5 text-center">The Closed Loop</h3>
      <div className="flex flex-wrap justify-center items-center gap-3">
        {[
          { step: 'Generate', icon: '🎨', desc: 'AI creates themes' },
          { step: 'Track', icon: '📊', desc: 'Collect all metrics' },
          { step: 'Evaluate', icon: '📐', desc: 'Score quality' },
          { step: 'Experiment', icon: '🧪', desc: 'A/B test variants' },
          { step: 'Optimize', icon: '⚡', desc: 'Improve prompts' },
        ].map((s, i) => (
          <React.Fragment key={i}>
            <div className="bg-white rounded-xl px-4 py-3 shadow-md border border-teal-200/50 text-center min-w-[110px] hover:scale-105 transition-transform duration-300">
              <span className="text-xl block">{s.icon}</span>
              <p className="text-sm font-bold text-gray-700 mt-1">{s.step}</p>
              <p className="text-xs text-gray-400">{s.desc}</p>
            </div>
            {i < 4 && <span className="text-teal-300 font-bold hidden md:block">→</span>}
          </React.Fragment>
        ))}
        <span className="text-teal-500 font-bold hidden md:block ml-1">↻</span>
      </div>
    </div>

    {/* Unified feedback table */}
    <div className="bg-gradient-to-br from-teal-50/50 to-emerald-50/50 rounded-2xl p-6 border border-teal-200/30 mb-6">
      <h3 className="text-base font-bold text-teal-600 uppercase tracking-wider mb-5 text-center">Unified Feedback Table: Single Source of Truth</h3>
      <p className="text-sm text-gray-500 text-center mb-4">One table captures every user metric. No scattered analytics. Everything queryable in one place.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { type: 'Quality Score', desc: 'Automated 5-dimension evaluation', icon: '🤖' },
          { type: 'Star Rating', desc: 'User star ratings (1-5)', icon: '⭐' },
          { type: 'Thumbs Up/Down', desc: 'Quick quality feedback', icon: '👍' },
          { type: 'Generated', desc: 'Theme generation event', icon: '🎨' },
          { type: 'Saved', desc: 'Theme save event', icon: '💾' },
          { type: 'Time Spent', desc: 'Per-page engagement duration', icon: '⏱️' },
          { type: 'Vendor Navigation', desc: 'Theme → vendor page click', icon: '🏪' },
          { type: 'Vendor Conversion', desc: 'Vendor page → view/shortlist', icon: '✅' },
          { type: 'Ritual Navigation', desc: 'Theme → ritual page click', icon: '📿' },
          { type: 'Ritual Conversion', desc: 'Ritual page → explore', icon: '🔍' },
          { type: 'Performance', desc: 'Response time, tokens, cost', icon: '⚡' },
          { type: 'Session', desc: 'Heartbeat, duration, page views', icon: '📋' },
        ].map((f, i) => (
          <div key={i} className="bg-white/80 rounded-lg p-3 border border-teal-100/50 text-center">
            <span className="text-lg block mb-1">{f.icon}</span>
            <p className="text-xs font-bold font-mono text-teal-700">{f.type}</p>
            <p className="text-xs text-gray-400 mt-0.5">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Conversion funnels */}
    <div className="bg-gradient-to-br from-teal-50/50 to-emerald-50/50 rounded-2xl p-6 border border-teal-200/30 mb-6">
      <h3 className="text-base font-bold text-teal-600 uppercase tracking-wider mb-5 text-center">Conversion Funnels</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Vendor funnel */}
        <div className="bg-white/80 rounded-xl p-5 border border-teal-200/50">
          <h4 className="font-bold text-gray-800 text-sm mb-3 flex items-center"><span className="mr-2">🏪</span>Vendor Funnel</h4>
          <div className="flex flex-col items-center gap-1">
            {[
              { step: 'Theme Generated', width: 'w-full' },
              { step: 'Navigate to Vendors', width: 'w-5/6' },
              { step: 'Vendor Page Visit', width: 'w-4/6' },
              { step: 'View Vendor Profile', width: 'w-3/6' },
              { step: 'Shortlist / Contact', width: 'w-2/6' },
            ].map((s, i) => (
              <div key={i} className={`${s.width} bg-gradient-to-r from-teal-100 to-emerald-100 rounded-lg px-3 py-2 text-center border border-teal-200/50`}>
                <p className="text-xs font-bold text-gray-700">{s.step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ritual funnel */}
        <div className="bg-white/80 rounded-xl p-5 border border-emerald-200/50">
          <h4 className="font-bold text-gray-800 text-sm mb-3 flex items-center"><span className="mr-2">📿</span>Ritual Funnel</h4>
          <div className="flex flex-col items-center gap-1">
            {[
              { step: 'Theme Generated', width: 'w-full' },
              { step: 'Navigate to Rituals', width: 'w-4/6' },
              { step: 'Ritual Page Visit', width: 'w-3/6' },
              { step: 'Save Ritual to Plan', width: 'w-2/6' },
            ].map((s, i) => (
              <div key={i} className={`${s.width} bg-gradient-to-r from-orange-100 to-red-100 rounded-lg px-3 py-2 text-center border border-orange-200/50`}>
                <p className="text-xs font-bold text-gray-700">{s.step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Session tracking & dedup engineering */}
    <div className="bg-gradient-to-r from-teal-50/80 to-emerald-50/80 rounded-2xl p-5 border border-teal-200/30">
      <h3 className="text-base font-bold text-teal-600 uppercase tracking-wider mb-3">🎯 Tracking Engineering</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Heartbeat-based session tracking', a: 'Periodic heartbeats detect active sessions. Tab visibility API pauses tracking when tab is hidden. Resumes on focus. Cross-tab sync via localStorage events.' },
          { q: 'Per-theme dedup logic', a: 'Navigation tracked per theme. Clicking "Find Vendors" from Theme A and Theme B counts as 2 navigations. Same theme twice counts as 1. Works for both saved and unsaved themes.' },
          { q: 'Conversion attribution', a: 'Every vendor/ritual page visit is linked back to the source theme that drove the navigation. Direct visits (not from a theme) are tracked separately and excluded from funnel conversion rates.' },
          { q: 'Why this feeds back', a: 'Save rates reveal which model users prefer. Time spent shows engagement quality. Funnel drop-offs identify UX issues. All data visible in Model Evaluation and A/B Testing dashboards.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-teal-100/50">
            <p className="text-sm font-bold text-teal-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section11Analytics;
